import { Activity, Smile, Users, Brain, MessageSquare, ClipboardCheck, Sparkles } from "lucide-react";
import {
  PageHero,
  Prose,
  SectionHeading,
  FeatureBand,
  IconCardGrid,
  CtaBand,
  RelatedLinks,
  mailto,
} from "@/components/site/PageParts";
import { Reveal } from "@/components/site/Reveal";
import { ASSETS } from "@/data/site";
import { Seo } from "@/components/site/Seo";

const BENEFITS = [
  { Icon: Activity, title: "Physical", body: "Improved balance, core strength, coordination and posture through the movement of the horse." },
  { Icon: Smile, title: "Emotional", body: "Growing confidence, calm and a real sense of achievement and joy." },
  { Icon: Users, title: "Social", body: "New friendships, teamwork and a warm, welcoming community." },
  { Icon: Brain, title: "Cognitive", body: "Better focus, communication and following of instructions." },
];

const STEPS = [
  { Icon: MessageSquare, title: "Get in touch", body: "Email us to tell us a little about the prospective rider." },
  { Icon: Users, title: "Chat with our coordinator", body: "We'll talk through needs, hopes and any support required." },
  { Icon: ClipboardCheck, title: "Come for an assessment", body: "A friendly assessment helps us match rider, horse and coach." },
  { Icon: Sparkles, title: "Start your journey", body: "Begin regular sessions and grow at your own pace." },
];

export default function RidingTherapy() {
  return (
    <div data-testid="riding-therapy-page">
      <Seo title="Riding & Therapy — RDA Glasgow" description="Discover the therapeutic benefits of horses at RDA Glasgow — how our sessions work, who they're for, and how to enquire. Places subject to availability and assessment." image={ASSETS.therapyCloseup} />
      <PageHero
        title="Riding & Therapy"
        subtitle="Discover the therapeutic power of horses — for body, mind and spirit."
        image={ASSETS.therapyCloseup}
        imageAlt="A close-up therapy moment between a rider and a horse"
      />

      <Prose>
        <SectionHeading eyebrow="What is RDA?" title="Therapy, freedom and fun — through horses" />
        <p>
          The Riding for the Disabled Association enables people with physical, sensory,
          cognitive and learning disabilities to enjoy the therapeutic benefits of
          horses. Riding and carriage driving can improve strength, coordination and
          confidence, while being wonderfully enjoyable.
        </p>
        <div className="rounded-2xl border border-rda-brown/30 bg-rda-brown/10 p-5 text-rda-ink">
          <strong className="font-semibold">Please note:</strong> Places are subject to
          availability and assessment.
        </div>
      </Prose>

      {/* What sessions look like */}
      <FeatureBand
        image={ASSETS.volunteering}
        imageAlt="A supported riding session in the indoor school at RDA Glasgow"
        title="What sessions look like"
        dark
      >
        {/* TODO: verify with RDA — exact session length and format */}
        <p>
          Sessions run as small groups or one-to-one, led by qualified coaches and
          supported by trained volunteers who walk alongside each rider. A typical
          session includes around 30 minutes of riding time, plus time to meet and
          settle with the horse.
        </p>
        <p>
          Every session is planned around the individual rider — their goals, their
          pace and what brings them the most benefit and enjoyment.
        </p>
      </FeatureBand>

      {/* Who it's for */}
      <Prose>
        <SectionHeading eyebrow="Who it's for" title="Riders of many ages and abilities" />
        <p>
          We welcome children and adults with a wide range of physical, sensory,
          cognitive and learning disabilities. An assessment helps us understand each
          rider's needs and make sure riding is safe and beneficial for them.
        </p>
      </Prose>

      {/* Benefits */}
      <section className="bg-rda-offwhite">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 md:py-20">
          <SectionHeading eyebrow="Benefits" title="The benefits of therapeutic riding" />
          <div className="mt-10">
            <IconCardGrid cards={BENEFITS} cols={4} />
          </div>
        </div>
      </section>

      {/* How to enquire */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 md:py-20">
          <SectionHeading eyebrow="How to enquire" title="Getting started is simple" />
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((step, i) => (
              <Reveal key={step.title} delay={i * 0.08}>
                <div className="h-full rounded-2xl border border-border bg-rda-offwhite p-7 shadow-soft">
                  <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-rda-teal text-white font-heading font-bold text-sm">
                      {i + 1}
                    </span>
                    <step.Icon className="h-5 w-5 text-rda-teal" />
                  </div>
                  <h3 className="mt-4 font-heading font-bold text-lg text-rda-ink">{step.title}</h3>
                  <p className="mt-2 text-sm text-rda-muted leading-relaxed">{step.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBand
        title="Ready to start your journey?"
        subtitle="Get in touch and our team will guide you through the next steps."
        ctaLabel="Enquire about riding"
        href={mailto("Riding enquiry")}
        testId="riding-cta-band"
      />

      <RelatedLinks
        links={[
          { title: "Volunteering", description: "Support riders as a side-walker, leader or helper.", to: "/volunteering" },
          { title: "About Us", description: "Learn more about RDA Glasgow and our centre.", to: "/about" },
          { title: "Support Us", description: "Help keep therapeutic riding available to all.", to: "/support-us" },
        ]}
      />
    </div>
  );
}
